import { darkThemeClass, lightThemeClass } from '../theme';
import { type Maybe } from '../types';
import { COOKIE_KEYS, parseCookie } from './cookies';
import { isServer } from './environment';

export enum Theme {
  LIGHT = 'light',
  DARK = 'dark',
  SYSTEM = 'system',
}

/**
 * To allow us to know as quickly as possible the users' theme preference
 * we use a cookie
 *
 * More discussion here on why we added this:
 * https://github.com/coin-tracker/coin-tracker-server/pull/11815
 */
export const getUserThemePreferenceFromCookie = () => {
  const cookie = parseCookie(document.cookie);
  return cookie?.[COOKIE_KEYS.theme];
};

export const userPrefersDarkTheme = () =>
  window.matchMedia('(prefers-color-scheme: dark)').matches;

interface getInitialThemeProps {
  themeOverridesPerPath: { pathname: string; theme: Theme }[];
  pathname: string;
}
export const getInitialTheme = ({
  themeOverridesPerPath,
  pathname,
}: getInitialThemeProps) => {
  if (isServer()) return null;
  const themeOverride = themeOverridesPerPath.find((override) =>
    override.pathname.includes(pathname),
  );

  if (themeOverride) {
    return themeOverride.theme;
  }

  let theme: Maybe<string> = getUserThemePreferenceFromCookie();

  theme = theme?.toLowerCase();
  if (theme === Theme.LIGHT) {
    return Theme.LIGHT;
  }

  if (theme === Theme.DARK) {
    return Theme.DARK;
  }

  if (userPrefersDarkTheme()) {
    return Theme.DARK;
  }
  return Theme.LIGHT;
};

export const addThemeClass = (theme: Theme) => {
  if (theme === Theme.LIGHT) {
    document.documentElement.classList.remove(darkThemeClass);
    document.documentElement.classList.add(lightThemeClass);
  } else {
    document.documentElement.classList.remove(lightThemeClass);
    document.documentElement.classList.add(darkThemeClass);
  }
};
